/**
 * Pure, React-free helpers for the Pre-Planning domain: id generation, month
 * parsing/formatting, month grouping (newest month first), external-link + draft
 * validation, blank/edit draft factories, and the pointer-row index-attribute
 * mapping used by {@link ./prePlanStore}.
 *
 * Keeping the index-attribute mapping (and its inverse) here — rather than
 * inside the store — makes it unit-testable without the API facade, and keeps
 * the store a thin data-access wrapper.
 *
 * Deliberately NO productivity/velocity metric: pre-plans are knowledge, never
 * counted or ranked per author.
 */

import { v4 as uuid } from 'uuid';
import type { StringMap } from '../../api';
import type {
  PrePlan,
  PrePlanDraft,
  PrePlanMonthGroup,
  PrePlanSummary,
} from './types';

/** Logical collection name for the generic document store. */
export const PRE_PLANS_COLLECTION = 'preplans';

/** Example external planning-doc link, used to seed the form placeholder. */
export const CHORUS_EXAMPLE_LINK =
  'https://chorus.aws.dev/doc/DBOGbTZAEoAq/Mercury-Sprint-Planning';

/** English month names — used for deterministic, locale-independent labels. */
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** Deterministic document id for a new pre-plan record. */
export function prePlanDocId(): string {
  return `preplan_${uuid()}`;
}

/** True when a string is a well-formed `YYYY-MM` month. */
export function isValidMonth(month: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test((month ?? '').trim());
}

/** The current month as `YYYY-MM` (local calendar). */
export function currentMonth(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/**
 * Formats a `YYYY-MM` month as a human label, e.g. `2026-08` → "August 2026".
 * Returns the input unchanged when it is not a valid month.
 */
export function formatMonth(month: string): string {
  const trimmed = (month ?? '').trim();
  if (!isValidMonth(trimmed)) return trimmed;
  const [year, mm] = trimmed.split('-');
  const name = MONTH_NAMES[Number(mm) - 1];
  return `${name} ${year}`;
}

/**
 * True when a string is a usable external link: an absolute http/https URL.
 * Blank is NOT valid here — callers treat externalLink as optional and only
 * validate it when present.
 */
export function isValidExternalLink(url: string): boolean {
  const candidate = (url ?? '').trim();
  if (!candidate) return false;
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Validates a draft for save. A pre-plan needs a title, a valid month, and
 * some Markdown content to be useful; the external link is optional but, when
 * provided, must be an http/https URL.
 */
export function validatePrePlanDraft(draft: PrePlanDraft): string[] {
  const problems: string[] = [];
  if (!draft.title.trim()) problems.push('A title is required.');
  if (!isValidMonth(draft.month)) {
    problems.push('A valid month (YYYY-MM) is required.');
  }
  if (!draft.content.trim()) {
    problems.push('Markdown content is required — the pre-plan renders it inline.');
  }
  const link = (draft.externalLink ?? '').trim();
  if (link && !isValidExternalLink(link)) {
    problems.push('The external link must be a valid http(s) URL.');
  }
  return problems;
}

/** Creates an empty draft for the create form (optionally seeding fields). */
export function blankPrePlanDraft(
  seed: Partial<PrePlanDraft> = {},
): PrePlanDraft {
  return {
    month: currentMonth(),
    title: '',
    sprintId: undefined,
    sprintName: undefined,
    content: '',
    externalLink: undefined,
    ...seed,
  };
}

/** Projects an existing pre-plan into an editable draft. */
export function draftFromPrePlan(plan: PrePlan): PrePlanDraft {
  return {
    month: plan.month,
    title: plan.title,
    sprintId: plan.sprintId,
    sprintName: plan.sprintName,
    content: plan.content,
    externalLink: plan.externalLink,
  };
}

/** Pointer-row index attribute keys (mirrored onto the DDB pointer row). */
export const PRE_PLAN_ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  month: 'month',
  title: 'title',
  sprintName: 'sprintName',
  externalLink: 'externalLink',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  lastUpdated: 'lastUpdated',
  deleted: 'deleted',
} as const;

/**
 * Builds the queryable index attributes mirrored onto the pointer row. The
 * Markdown `content` and `sprintId` are intentionally NOT mirrored (they live
 * in the S3 body); the list view reconstructs summaries from these attributes
 * without hydrating S3.
 */
export function prePlanIndexAttributes(plan: PrePlan): StringMap {
  return {
    [PRE_PLAN_ROW.month]: plan.month ?? '',
    [PRE_PLAN_ROW.title]: plan.title ?? '',
    [PRE_PLAN_ROW.sprintName]: plan.sprintName ?? '',
    [PRE_PLAN_ROW.externalLink]: plan.externalLink ?? '',
    [PRE_PLAN_ROW.createdBy]: plan.createdBy ?? '',
    [PRE_PLAN_ROW.createdAt]: plan.createdAt ?? '',
    [PRE_PLAN_ROW.lastUpdated]: plan.lastUpdated ?? '',
    [PRE_PLAN_ROW.deleted]: plan.deleted ? 'true' : 'false',
  };
}

/** Reconstructs a pre-plan summary from a pointer row. */
export function prePlanSummaryFromRow(row: StringMap): PrePlanSummary {
  return {
    id: row[PRE_PLAN_ROW.primaryIdentifier] ?? '',
    month: row[PRE_PLAN_ROW.month] ?? '',
    title: row[PRE_PLAN_ROW.title] ?? '',
    sprintName: row[PRE_PLAN_ROW.sprintName] || undefined,
    externalLink: row[PRE_PLAN_ROW.externalLink] || undefined,
    createdBy: row[PRE_PLAN_ROW.createdBy] ?? '',
    createdAt: row[PRE_PLAN_ROW.createdAt] ?? '',
    lastUpdated: row[PRE_PLAN_ROW.lastUpdated] ?? '',
  };
}

/** Sorts summaries newest-first (by lastUpdated, then createdAt). */
export function sortPrePlansByRecency(
  items: PrePlanSummary[],
): PrePlanSummary[] {
  return items
    .slice()
    .sort(
      (a, b) =>
        (b.lastUpdated ?? '').localeCompare(a.lastUpdated ?? '') ||
        (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
    );
}

/**
 * Groups pre-plan summaries BY MONTH, with months ordered newest-first and each
 * month's entries sorted newest-first. Entries with an invalid/blank month are
 * collected under an "Undated" group placed last.
 */
export function groupByMonthDesc(
  items: PrePlanSummary[],
): PrePlanMonthGroup[] {
  const byMonth = new Map<string, PrePlanSummary[]>();
  for (const item of items) {
    const key = isValidMonth(item.month) ? item.month : '';
    const bucket = byMonth.get(key);
    if (bucket) bucket.push(item);
    else byMonth.set(key, [item]);
  }

  const keys = Array.from(byMonth.keys()).sort((a, b) => {
    // Undated ('') sorts last; otherwise newest month first.
    if (a === '') return 1;
    if (b === '') return -1;
    return b.localeCompare(a);
  });

  return keys.map((month) => ({
    month,
    label: month === '' ? 'Undated' : formatMonth(month),
    items: sortPrePlansByRecency(byMonth.get(month) ?? []),
  }));
}
